/**
 * MoveArrow Component
 * 
 * Renders an arrow visualization between two squares on a chess board
 * to indicate a move that was played or suggested.
 */

import React from 'react';
import styles from './MoveArrow.module.css';

export interface MoveArrowProps {
  /** Start square in algebraic notation (e.g., 'e2') */
  from: string;
  /** End square in algebraic notation (e.g., 'e4') */
  to: string;
  /** Type of arrow (last move, suggested move, etc.) */
  type?: 'lastMove' | 'suggestion' | 'hint';
  /** Size of the chess board in pixels */
  boardSize: number;
  /** Optional className for custom styling */
  className?: string;
}

/**
 * Calculates the position and angle for an arrow between two chess squares
 */
const calculateArrowProperties = (
  from: string, 
  to: string, 
  boardSize: number
): { startX: number; startY: number; endX: number; endY: number; angle: number; length: number } => {
  // Convert algebraic notation to coordinates
  const fromFile = from.charCodeAt(0) - 97; // 'a' = 0, 'b' = 1, etc.
  const fromRank = 8 - parseInt(from[1]); // '8' = 0, '7' = 1, etc.
  const toFile = to.charCodeAt(0) - 97;
  const toRank = 8 - parseInt(to[1]);
  
  // Calculate square size
  const squareSize = boardSize / 8;
  
  // Calculate center coordinates of the squares
  const startX = (fromFile * squareSize) + (squareSize / 2);
  const startY = (fromRank * squareSize) + (squareSize / 2);
  const endX = (toFile * squareSize) + (squareSize / 2);
  const endY = (toRank * squareSize) + (squareSize / 2);
  
  // Calculate angle and length
  const dx = endX - startX;
  const dy = endY - startY;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  const length = Math.sqrt(dx * dx + dy * dy);
  
  return { startX, startY, endX, endY, angle, length };
};

/**
 * MoveArrow - Component to visualize a chess move as an arrow
 */
const MoveArrow: React.FC<MoveArrowProps> = ({
  from,
  to,
  type = 'lastMove',
  boardSize,
  className,
}) => {
  // If from and to are the same, don't render an arrow
  if (from === to) return null;

  const { startX, startY, endX, endY, angle, length } = calculateArrowProperties(from, to, boardSize);
  
  // Adjust the arrow to be slightly shorter than the full distance
  // This prevents the arrow from extending to the edge of the squares
  const shortenFactor = 0.75;
  const shortenedLength = length * shortenFactor;
  
  // Calculate the offset to center the arrow in its container
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  
  // Adjust position to account for shortened length
  const adjustedX = midX - (shortenedLength / 2);
  const adjustedY = midY - (3 / 2); // Arrow height is typically ~3px
  
  const arrowStyle: React.CSSProperties = {
    width: `${shortenedLength}px`,
    transform: `translate(${adjustedX}px, ${adjustedY}px) rotate(${angle}deg)`,
  };
  
  const arrowClasses = [
    styles.moveArrow,
    styles[type],
    className,
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={arrowClasses}
      style={arrowStyle}
      data-testid={`move-arrow-${from}-${to}`}
      role="img"
      aria-label={`Move from ${from} to ${to}`}
    />
  );
};

export default MoveArrow; 